import { RECIPIENT } from '@/karehero-app/tell-us-once/intake/content';
import { type WalkthroughSlide } from '@/karehero-app/tell-us-once/walkthrough/OnboardingWalkthrough.content';
import { CIRCLE_SWITCHER } from '@/karehero-app/shell/CircleSwitcher.content';

const R = RECIPIENT;

/** The Care hub concept's copy. Section by section, top to bottom. */
export const CARE_HUB = {
  /** A started form's chip: a status, never a count of questions. */
  started: 'In progress',
  people: 'Who you care for',
  /** The summary, said as a sentence, when the card has no heading over it. */
  youCareFor: (name: string, summary: string) =>
    `You care for ${name}, who is ${summary}`,
  /** SANDBOX: the llama page's button in place of the heading. */
  talkToKeni: 'Talk to Keni',
  /** Concept 2's Home: everyone's actions in one list. */
  todo: 'To do',
  /** The switcher's own words, so it can't be read as adding to the circle. */
  add: CIRCLE_SWITCHER.add,
  /**
   * Who they are, in a line or two, from what onboarding heard. Prose in the
   * prototype, generated in the app.
   */
  summary: {
    [R]: '83, with vascular dementia and osteoarthritis. Walks with a frame and needs help day and night.',
  } as Record<string, string>,
  noSummary: (name: string) =>
    `We don't know much about ${name} yet. Tell us about them and we'll fill this in.`,
  circle: (name: string) => `${name}’s care circle`,
  /** The circle pill when nobody is in it yet. */
  invite: 'Invite',
  inviteLabel: (name: string) => `Invite people to ${name}’s care circle`,
  /** The circle as a row on their About page, which already names them. */
  circleRow: 'Care circle',
  circleWhat: 'Family and friends who help you care.',
  couldGet: (name: string) => `What ${name} could get`,
  seeAll: (n: number) => `See all ${n}`,
  forms: 'Forms',
  money: 'Money and forms',
  everyForm: 'See every form',
  arranging: 'Arranging care',
  shortlists: {
    icon: 'bookmark',
    name: 'Shortlists',
    description: 'Picked for you by your care expert',
  },
  record: (name: string) => `About ${name}`,
  /** The cards by subject. */
  subjects: { money: 'Money', legal: 'Legal' },
  /** Money's page, by section. */
  moneyPage: {
    others: 'Other benefits',
    /** Under what they could get: the rest, on a page of their own. */
    viewOthers: 'View other benefits',
    all: 'Benefits',
    finances: 'Finances',
    howMuch: (name: string) => `Helps work out how much ${name} could get.`,
  },
  /** The calculator on Home, before the check, as a prompt. */
  checkPrompt: (name: string) => `Check what ${name} could get`,
  /** The care assessment, on Home and in Arranging care. */
  assessment: (name: string) =>
    `Tell us about ${name}'s care, so a care expert can work out what kind they need, like help at home or a care home.`,
  /**
   * The lead on Home, where it says something other than the row it opens.
   * Anything not here reads as it does in its area.
   */
  lead: {
    call: 'Book a call',
    afterFinances: (name: string) =>
      `You've told us about ${name}'s finances. A care expert can talk you through the best next step.`,
    afterAssessment: (name: string) =>
      `Now we know about ${name}'s care, a care expert can help you start arranging it.`,
  },
  /**
   * Money, arrived at from the end of the calculator: what it found, said once
   * at the top. A later visit doesn't show it.
   */
  arrived: {
    name: (name: string, count: number) =>
      `It looks like ${name} could get help with ${count} things`,
    description: 'Thanks for answering all the questions.',
  },
  /** The calculator after the check, under the results: a way to update it. */
  recheck: {
    name: 'Change what you told us',
    description:
      "If something's changed, update your answers and we'll check again.",
  },
  /** The line under it, the same on Home and in Money. */
  checkLine: (name: string) =>
    `A few quick questions so we can work out what you and ${name} might be eligible for.`,
  /** Power of attorney on the Legal page, before and once they have it. */
  poa: {
    name: 'Power of attorney',
    what: (name: string) =>
      `Apply for power of attorney, so you or someone you trust can make decisions if ${name} can't.`,
    sheetTitle: 'What is power of attorney?',
    about: (name: string) =>
      `A lasting power of attorney lets ${name} choose someone they trust to make decisions for them, if a day comes when they can't. There are two kinds: one for health and care, and one for money and property. It has to be set up while ${name} can still decide.`,
    start: 'Start setting it up',
    inPlace: 'You have it for health and care, and for money and property.',
    inPlaceChip: 'In place',
  },
  /** Legal's way to a care expert, under power of attorney. */
  legalCall: (name: string) =>
    `Not sure what ${name} needs? A care expert can talk it through with you.`,
  /** Concept 3's heading over the area cards. */
  areasTitle: (name: string) => `${name}’s care`,
  /** Concept 3's card icons, one per area. */
  areaIcons: {
    could: 'sterling-sign',
    money: 'sterling-sign',
    legal: 'file-contract',
    forms: 'clipboard-list',
    arranging: 'magnifying-glass',
    record: 'file-lines',
  },
  /** The profile's own tabs, as rows. Named by the tabs, so nothing forks. */
  recordRows: [
    {
      id: 'profile',
      icon: 'clipboard-list',
      what: 'Their details, health and the help they need.',
    },
    {
      id: 'docs',
      icon: 'file-lines',
      what: 'Letters, assessments and forms, kept in one place.',
    },
    {
      id: 'calls',
      icon: 'phone',
      what: 'Notes from your calls with our care experts.',
    },
  ] as const,
};

/** Who has been through the eligibility check. Only Margaret, for now. */
export const CHECKED = [R];

/** How many results the hub shows before See all. */
export const HUB_MAX = 3;

/** Why each screen on the Care hub's top row (**bold** marks the key part) is the way it is. The flows page
 * shows it under each frame; the walkthrough presents it a slide at a time. */
export const WHY = {
  first: [
    '**A card at the top tells us who the care recipient is**, with a short summary.',
    "**Underneath, there's a way to add someone else they care for.** They probably won't have a second person at this point.",
    "**Invite adds people to this person's care circle.** They probably won't have anyone in it yet, so Invite sits where the faces would be.",
    "**Instead of approaching things by the name of the form and putting every form on the front**, I've arranged them into Money, where the benefits and most forms live; Arranging care, for care search; Legal, where power of attorney lives; and About, for everything about the care recipient.",
  ],
  home: [
    '**Everything on this page is specific to one care recipient.** That was a big reason behind this design. Everything else, like Actions and Help, can cover everyone they care for.',
    '**Once people join the care circle, their faces show instead of Invite.**',
    '**Anyone else they care for sits underneath.**',
    "**If they pick someone else, everything below changes.** The reason I've done this is it keeps anything that's custom to a care circle on the right page.",
  ],
  before: [
    '**The middle highlights the most important action for them right now.** It can come from onboarding, or as we get chatting.',
    "**It shows one call to action, their primary one, in the right order, because they might have more than one.** What's most important if someone needs help with everything is a product question we'll need to work on.",
    "**It's a shortcut, not a permanent home.** The benefits calculator lives in Money, because it's to do with money.",
  ],
  moneyBefore: [
    "**Money is where it lives permanently.** I'm using the cherry again to highlight it, the same as on Home.",
    "**Whether they open it from Home or from Money, it lands them in the same place.** Wherever they get dropped off, they learn that's where it lives.",
    "**If they haven't checked yet, it's the primary call to action at the top.** Then all the benefits we have. That's a lot, so we'll need a show more, but that's not the point right now.",
  ],
  moneyAfter: [
    "**Once they've done the calculator, Financial status becomes the main call to action**, in cherry like the check was. It gathers information to help with their call with CAM, until Tell Us Once is applied more widely.",
    '**Change what you told us sits above it**, so they can update their answers and check again.',
    '**Green highlights the ones they can get.** Coral is something in progress, like Attendance Allowance, hopefully with some answers already given by Tell Us Once.',
  ],
  arranging: [
    "**The hub for anything to do with the care assessment.** We're keeping the functionality we have now, but fitting it all in.",
  ],
  legal: [
    '**Power of attorney lives here**, with a quick way to find out what it is.',
  ],
  legalHas: [
    "**If they've told us they have power of attorney, the card says it's in place.**",
  ],
  record: [
    '**About is where everything about the care recipient lives**, along with their documents and call notes.',
  ],
};

/** The Care hub, presented: a slide a screen, in the order of the top row. */
export const CARE_HUB_SLIDES: WalkthroughSlide[] = [
  // A title to talk over, with no phone.
  {
    question: 'Where does everything go?',
    intro:
      "**My aim was to make the landing page friendlier and more obvious, so people don't have to think about where to go.** Profile felt far away, it wasn't clear what a profile means in care, and it had become a dumping ground for features. Care felt far away too. Everything our app does relates to the care recipient, so it doesn't need splitting up or to live in two places. It should be obvious at a glance what themes are available, without clicking around.",
    groups: [
      {
        heading: 'Solving for',
        points: [
          'How does Tell Us Once fit with the parts of the app people still do by hand?',
          'How do we make it obvious what someone can do here?',
          'How does the app adapt to what each person needs?',
          'How do we surface what we learn about them?',
          'How do we design something that scales as we add features and care circles?',
        ],
      },
    ],
  },
  {
    question: 'Home, just them, no circle yet',
    points: WHY.first,
    screen: 'first',
  },
  { question: 'Home, as their circle grows', points: WHY.home, screen: 'home' },
  { question: 'Home, before the check', points: WHY.before, screen: 'before' },
  {
    question: 'Money, before the check',
    points: WHY.moneyBefore,
    screen: 'moneyBefore',
  },
  {
    question: 'Into the calculator',
    points: [
      '**Check what Margaret could get opens our version 1 draft of the benefits calculator chat.**',
    ],
    screenRow: [
      { screen: 'homeCheck', label: 'Home, with the check' },
      { screen: 'calculator', label: 'Benefits calculator, version 1 draft' },
    ],
  },
  {
    question: 'Into Money',
    points: [
      "**See your results takes them to Money, where the results now live.** It lands them in the same place they'd find them later.",
    ],
    screenRow: [
      { screen: 'results', label: 'The end of the chat' },
      { screen: 'moneyResults', label: 'Money, after the check' },
    ],
  },
  {
    question: 'Money, after the check',
    points: WHY.moneyAfter,
    screen: 'moneyAfter',
  },
  { question: 'Arranging care', points: WHY.arranging, screen: 'arranging' },
  { question: 'Legal', points: WHY.legal, screen: 'legal' },
  {
    question: 'Legal, if they have it',
    points: WHY.legalHas,
    screen: 'legalHas',
  },
  { question: 'About them', points: WHY.record, screen: 'record' },
];
